import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ImageOff, Eye, Users, Coins, Star, Pencil, Globe, Youtube, Instagram, Twitter, Music2, ArrowLeft, UserPlus, UserCheck, Heart } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StarRating from "@/components/StarRating";
import CreatorPageEditModal from "@/components/CreatorPageEditModal";
import { toast } from "sonner";

const LINK_ICONS = { website: Globe, youtube: Youtube, instagram: Instagram, twitter: Twitter, tiktok: Music2 };

const StatChip = ({ icon: Icon, value, label }) => (
  <div className="flex flex-col items-center">
    <div className="flex items-center gap-1 text-lg font-extrabold text-white tabular-nums">
      <Icon size={16} className="text-[#8fbf82]" />{value}
    </div>
    <div className="text-[11px] font-semibold text-white/50 uppercase tracking-wide">{label}</div>
  </div>
);

export default function CreatorPage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(1);
  const [editOpen, setEditOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/creators/${username}?page=${page}`)
      .then(({ data }) => { setData(data); setNotFound(false); setFollowing(!!data.is_following); setFollowers(data.stats?.followers || 0); })
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [username, page]);

  useEffect(() => { load(); }, [load]);

  const toggleFollow = async () => {
    if (!user) { navigate("/login"); return; }
    setFollowBusy(true);
    try {
      const { data } = following
        ? await api.delete(`/creators/${username}/follow`)
        : await api.post(`/creators/${username}/follow`);
      setFollowing(data.following);
      setFollowers(data.followers);
      if (data.following) toast.success(`Following ${data_display()} 🎬`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update follow");
    } finally {
      setFollowBusy(false);
    }
  };
  const data_display = () => (data?.page?.display_name || "creator");

  const isOwner = !!(user && data && user.username === data.username);
  const accent = data?.page?.accent_color || "#4f7746";

  if (loading && !data) {
    return <div className="min-h-screen bg-black py-32 flex justify-center"><Loader2 size={30} className="spin text-[#8fbf82]" /></div>;
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="sms-tweed max-w-lg w-full rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] px-6 py-16 text-center" data-testid="creator-notfound" style={{ boxShadow: "0 0 44px -14px rgba(93,138,82,.5)" }}>
          <div className="w-14 h-14 rounded-full bg-black border-2 border-[#5d8a52] flex items-center justify-center mx-auto mb-4"><Users size={26} className="text-[#8fbf82]" /></div>
          <h2 className="text-xl font-extrabold text-white">This channel isn't available yet</h2>
          <p className="text-sm text-white/60 mt-2">Creators unlock their own page once they've published 10 videos to the Community.</p>
          <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-full" style={{ background: "#4f7746", border: "2px solid #5d8a52" }}>
            <ArrowLeft size={16} /> Back to Community
          </button>
        </div>
      </div>
    );
  }

  const p = data.page;
  const stats = data.stats;
  const items = data.videos.items;
  const av = p.avatar;
  const links = Object.entries(p.links || {}).filter(([, v]) => v);

  return (
    <div className="min-h-screen bg-black py-5 sm:py-8 px-3 sm:px-4" data-testid="creator-page">
      <div className="sms-tweed max-w-5xl mx-auto rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] overflow-hidden" style={{ boxShadow: "0 30px 90px -30px rgba(0,0,0,.85), 0 0 46px -16px rgba(93,138,82,.45)" }}>
        {/* Banner */}
        <div className="relative w-full aspect-[4/1] max-h-[280px] overflow-hidden" data-testid="creator-banner">
          {p.banner_id ? (
            <img src={imageUrl(p.banner_id)} alt="banner" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${accent}, #0f0f16)` }} />
          )}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(15,15,22,0.85))" }} />
        </div>

        {/* Identity header */}
        <div className="px-5 sm:px-8 pb-8">
          <div className="relative -mt-6 sm:-mt-8 flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-start gap-3">
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-white truncate" data-testid="creator-name">{p.display_name}</h1>
                  <p className="text-sm text-white/50">@{data.username}</p>
                </div>
                {isOwner ? (
                  <button data-testid="creator-edit-btn" onClick={() => setEditOpen(true)}
                    className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-white px-3.5 py-2 rounded-full transition-transform hover:scale-105"
                    style={{ background: "#4f7746", border: "2px solid #5d8a52" }}>
                    <Pencil size={13} /> Edit page
                  </button>
                ) : !data.is_self && (
                  <button data-testid="creator-follow-btn" onClick={toggleFollow} disabled={followBusy}
                    className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full transition-transform hover:scale-105 disabled:opacity-60"
                    style={following ? { background: "transparent", color: "#8fbf82", border: "2px solid #5d8a52" } : { background: accent, color: "#fff", border: "2px solid #5d8a52" }}>
                    {following ? <><UserCheck size={15} /> Following</> : <><UserPlus size={15} /> Follow</>}
                  </button>
                )}
              </div>
            </div>
          </div>

          {p.bio && <p className="mt-4 text-sm sm:text-base text-white/75 max-w-2xl whitespace-pre-line" data-testid="creator-page-bio">{p.bio}</p>}

          {/* Stats + links */}
          <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-4">
            <StatChip icon={Users} value={stats.videos} label="Videos" />
            <StatChip icon={UserPlus} value={followers} label="Followers" />
            <StatChip icon={Eye} value={stats.views} label="Views" />
            <StatChip icon={Star} value={stats.avg_rating || "—"} label="Avg rating" />
            <StatChip icon={Heart} value={stats.loves || 0} label="Love" />
            {links.length > 0 && (
              <div className="flex items-center gap-2">
                {links.map(([key, url]) => {
                  const Icon = LINK_ICONS[key] || Globe;
                  return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`creator-link-out-${key}`}
                      className="w-9 h-9 rounded-full bg-black border border-[#5d8a52] flex items-center justify-center text-[#8fbf82] hover:bg-[#5d8a52]/20 transition-colors">
                      <Icon size={17} />
                    </a>
                  );
                })}
              </div>
            )}
            {av?.tag && (
              <div className="ml-auto self-end flex items-baseline gap-1 pr-1" data-testid="creator-avatar">
                <span className="text-2xl leading-none">{av?.emoji || "🎬"}</span>
                <span className="text-lg font-extrabold tabular-nums" style={{ color: accent }}>{av?.tag}</span>
              </div>
            )}
          </div>

          <div className="h-px bg-white/10 my-6" />

          {/* Videos grid */}
          <h2 className="text-lg font-extrabold text-white mb-3">Videos</h2>
          {items.length === 0 ? (
            <div className="py-12 text-center text-white/50">No videos yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 sm:gap-3">
              {items.map((m, i) => (
                <div key={m.id} data-testid={`creator-video-${m.id}`} onClick={() => navigate(`/community/${m.id}`)}
                  className="sms-tweed rounded-2xl overflow-hidden text-left group cursor-pointer sms-fade-up bg-[#0a0a0f] border-2 transition-transform hover:scale-[1.02]" style={{ animationDelay: `${i * 0.03}s`, borderColor: "#5d8a52" }}>
                  <div className="w-full aspect-video bg-black overflow-hidden relative">
                    {m.hero_image_id ? (
                      <img src={imageUrl(m.hero_image_id)} alt={m.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/25"><ImageOff size={22} /></div>
                    )}
                    {m.views > 0 && (
                      <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1 text-[10px] font-bold text-white bg-black/65 px-1.5 py-0.5 rounded-full"><Eye size={11} />{m.views}</span>
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className="inline-block text-[11px] font-extrabold uppercase tracking-wide text-black bg-[#ffd21e] px-2 py-0.5 rounded-full">{m.category}</div>
                    <h3 className="text-sm font-bold text-white leading-snug line-clamp-2 mt-1.5">{m.title}</h3>
                    <div className="mt-1.5"><StarRating value={m.rating_avg} count={m.rating_count} size={12} /></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {data.videos.pages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-sm font-bold px-4 py-2 rounded-full border-2 border-[#5d8a52] text-[#8fbf82] hover:bg-[#5d8a52]/15 disabled:opacity-40 transition-colors">Prev</button>
              <span className="text-sm font-semibold text-white/50 px-2 py-2">Page {page} / {data.videos.pages}</span>
              <button disabled={page >= data.videos.pages} onClick={() => setPage((p) => p + 1)} className="text-sm font-bold px-4 py-2 rounded-full border-2 border-[#5d8a52] text-[#8fbf82] hover:bg-[#5d8a52]/15 disabled:opacity-40 transition-colors">Next</button>
            </div>
          )}
        </div>
      </div>

      <CreatorPageEditModal open={editOpen} onClose={() => setEditOpen(false)} page={p} onSaved={() => load()} />
    </div>
  );
}
